import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, X, Check } from 'lucide-react';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const SEVERITY_BG: Record<string, string> = {
  critical: 'bg-danger/20 border-danger',
  error:    'bg-danger/10 border-danger',
  warn:     'bg-warning/10 border-warning',
  info:     'bg-elevated border-border',
};

export default function NotificationBanner({ category }: { category?: string }) {
  const qc = useQueryClient();

  const list = useQuery<any[]>({
    queryKey: ['admin-notifications', category || 'all'],
    queryFn: () => fJson('/api/admin/notifications?unread_only=1'),
    refetchInterval: 30000,
  });

  // Rules of Hooks: ALL hooks must run on every render, before any early return.
  const ack = useMutation({
    mutationFn: (id: number) => fJson(`/api/admin/notifications/${id}/ack`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-notifications'] }),
  });
  const dismiss = useMutation({
    mutationFn: (id: number) => fJson(`/api/admin/notifications/${id}/dismiss`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-notifications'] }),
  });

  const filtered = (list.data || []).filter((n) => !category || n.category === category).slice(0, 5);
  if (!filtered.length) return null;

  return (
    <div className="space-y-2">
      {filtered.map((n) => (
        <div
          key={n.notification_id}
          className={`card p-3 border-l-4 flex items-start gap-3 ${SEVERITY_BG[n.severity] || SEVERITY_BG.info}`}
        >
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0 text-warning" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">{n.title}</div>
            {n.body && <div className="text-xs text-textSecondary mt-1 whitespace-pre-line">{n.body}</div>}
            <div className="text-xs text-textSecondary mt-1">{new Date(n.created_at).toLocaleString()}</div>
          </div>
          <div className="flex gap-1 flex-shrink-0">
            <button title="Acknowledge" className="btn-ghost p-1" onClick={() => ack.mutate(n.notification_id)}>
              <Check size={14} />
            </button>
            <button title="Dismiss" className="btn-ghost p-1" onClick={() => dismiss.mutate(n.notification_id)}>
              <X size={14} />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
